import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UILanguage, SUPPORTED_UI_LANGUAGES } from '../i18n/config';

interface SettingsContextType {
  uiLanguage: UILanguage;
  setUILanguage: (language: UILanguage) => void;
  availableUILanguages: typeof SUPPORTED_UI_LANGUAGES;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  // Initialize UI language from i18next (which already detected from browser/localStorage)
  const [uiLanguage, setUILanguageState] = useState<UILanguage>(() => {
    const currentLang = i18n.language as UILanguage;
    // Validate that the language is supported
    if (currentLang in SUPPORTED_UI_LANGUAGES) {
      return currentLang;
    }
    // Fallback to English if unsupported
    return 'en';
  });

  // Update UI language and persist to i18next
  const setUILanguage = (language: UILanguage) => {
    setUILanguageState(language);
    i18n.changeLanguage(language);
    // i18next will automatically persist to localStorage via its detector plugin
  };

  // Sync state with i18next language changes (e.g., from other tabs)
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (lng in SUPPORTED_UI_LANGUAGES) {
        setUILanguageState(lng as UILanguage);
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // Update HTML lang attribute when UI language changes
  useEffect(() => {
    document.documentElement.lang = uiLanguage;
  }, [uiLanguage]);

  const value: SettingsContextType = {
    uiLanguage,
    setUILanguage,
    availableUILanguages: SUPPORTED_UI_LANGUAGES,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
